import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { authService } from '../api/auth';
import { clearActiveContext } from '../api/context';
import { logout } from './authSlice';
import type { AppDispatch } from '../../../store/store';

/**
 * Shown on the sign-in screen when this browser is signed out but the server
 * could not confirm the session is dead elsewhere. On a shared computer the
 * difference between "signed out" and "signed out here" is the whole point of
 * pressing the button, so it is said rather than swallowed.
 */
export const SIGNED_OUT_HERE_ONLY =
  'Cerramos tu sesión en este dispositivo, pero no pudimos confirmarlo con el servidor. Si usas una computadora compartida, cambia tu contraseña.';

/**
 * The only shape of state this needs. Typed locally rather than through
 * RootState, because the store imports the slices and this would close the
 * loop.
 */
interface SignOutState {
  auth: { user: { role: string } | null };
  context?: { kidMode: boolean };
}

/**
 * PRT-17-FR-01 — the one sign-out routine.
 *
 * There were four. App's menu revoked the session server-side; the family,
 * teacher and admin consoles (ConsoleShell, PortalShell) and the child space
 * (KidShell) only dispatched `logout()`, so "Cerrar sesión" there forgot the
 * token in this browser and left the Cognito session valid everywhere else.
 * Every path now goes through here, so the next one cannot drift.
 *
 * Order matters and every step is best-effort except the last:
 *  1. PARENT only — forget the active learner on the server
 *     (DELETE /context/active). 12 Oct 2026 product decision on PRT-08-FR-07
 *     (shared family devices): the next sign-in starts in the parent view, not
 *     in whichever child was active when the last person left. It runs before
 *     the revoke because afterwards there is no token to send it with.
 *     Not in kid mode: DEC-S2-S01-06, a Student Sign Out never falls back to
 *     Parent. The server refuses the delete and the lock stays, so the next
 *     sign-in on this device lands back in the child's space.
 *  2. Revoke server-side. The API clears the tokens whether or not this
 *     succeeds. A Parent: POST /auth/logout → Cognito GlobalSignOut. In kid
 *     mode: POST /context/kid-mode/sign-out, which ends this device only; the
 *     Parent's other devices are not the Student's to end (§VI.1).
 *  3. Clear Redux. The learner context leaves with `logout` (contextSlice).
 *
 * Neither server call can stop step 3: a parent who cannot reach us must still
 * be able to sign out of this browser.
 */
export const signOut = createAsyncThunk<{ revoked: boolean }, void, { state: SignOutState }>(
  'auth/signOut',
  async (_, { getState, dispatch }) => {
    const state = getState();
    if (state.auth.user?.role === 'PARENT' && !state.context?.kidMode) {
      try {
        await clearActiveContext();
      } catch {
        // Best-effort by decision: the server reads the context back with
        // ownership revalidated, so a record left behind is a convenience
        // lost, not an access granted. Blocking sign-out on it would be.
      }
    }

    let revoked = false;
    try {
      ({ revoked } = await (state.context?.kidMode
        ? authService.signOutStudent()
        : authService.logout()));
    } catch {
      // authService.logout reports rather than throws; this is the guard for
      // the day it does not.
    } finally {
      dispatch(logout());
    }
    return { revoked };
  },
);

/**
 * Sign out and land on /login — what every "Cerrar sesión" button calls.
 *
 * Returns whether the server confirmed revocation, for a caller that wants to
 * say more; the sign-in screen already carries the notice when it did not.
 */
export function useSignOut(): () => Promise<{ revoked: boolean }> {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  return useCallback(async () => {
    const result = await dispatch(signOut());
    const revoked = signOut.fulfilled.match(result) && result.payload.revoked;
    navigate(
      '/login',
      revoked
        ? { replace: true }
        : { replace: true, state: { signOutNotice: SIGNED_OUT_HERE_ONLY } },
    );
    return { revoked };
  }, [dispatch, navigate]);
}
